// src/components/intelligence/LiveInsights.tsx
import React, { useState, useEffect, useRef } from "react";
import { AlertTriangle, TrendingUp, Users, ArrowRight, Zap, Eye, ShoppingBag, RefreshCw } from "lucide-react";
import { clsx } from "clsx";
import { RETAILERS } from "@/data";

// ── Feed entry types ──────────────────────────────────────────
type SignalType = "upsell" | "competitor" | "crosssell" | "reengagement";
type LoyaltyTier = "Platinum" | "Gold" | "Silver";

interface FeedEntry {
  id:           string;
  memberId:     string;
  tier:         LoyaltyTier;
  lastPurchase: string;
  topic:        string;
  signalType:   SignalType;
  intentScore:  number;
  competitor?:  string;
  action:       string;
}

// ── Retailer-specific feed data ───────────────────────────────
const FEED_DATA: Record<string, FeedEntry[]> = {
  "org-mhw": [
    { id:"f01", memberId:"M-****42", tier:"Gold",     lastPurchase:"34 days ago",  topic:"Power Tools",          signalType:"upsell",       intentScore:84, action:"Add to Campaign"  },
    { id:"f02", memberId:"M-****17", tier:"Platinum", lastPurchase:"8 days ago",   topic:"Home Depot — Drills",  signalType:"competitor",   intentScore:91, competitor:"Home Depot",   action:"Retention Alert"  },
    { id:"f03", memberId:"M-****88", tier:"Silver",   lastPurchase:"62 days ago",  topic:"Smart Home Devices",   signalType:"crosssell",    intentScore:72, action:"Cross-Sell Push"  },
    { id:"f04", memberId:"M-****33", tier:"Gold",     lastPurchase:"91 days ago",  topic:"Lowe's Power Tools",   signalType:"competitor",   intentScore:88, competitor:"Lowe's",       action:"Win-Back Now"     },
    { id:"f05", memberId:"M-****56", tier:"Platinum", lastPurchase:"3 days ago",   topic:"Outdoor & Garden",     signalType:"upsell",       intentScore:76, action:"Add to Campaign"  },
    { id:"f06", memberId:"M-****21", tier:"Silver",   lastPurchase:"44 days ago",  topic:"Electrical Supplies",  signalType:"crosssell",    intentScore:68, action:"Cross-Sell Push"  },
    { id:"f07", memberId:"M-****94", tier:"Gold",     lastPurchase:"120 days ago", topic:"Home Depot — Paint",   signalType:"reengagement", intentScore:79, competitor:"Home Depot",   action:"Re-Engage Now"    },
    { id:"f08", memberId:"M-****67", tier:"Platinum", lastPurchase:"12 days ago",  topic:"Power Tool Accessories",signalType:"upsell",      intentScore:82, action:"Upsell Bundle"    },
    { id:"f09", memberId:"M-****39", tier:"Gold",     lastPurchase:"28 days ago",  topic:"Lowe's — Plumbing",    signalType:"competitor",   intentScore:85, competitor:"Lowe's",       action:"Retention Alert"  },
    { id:"f10", memberId:"M-****73", tier:"Silver",   lastPurchase:"55 days ago",  topic:"Home Renovation",      signalType:"crosssell",    intentScore:71, action:"Inspire Campaign" },
    { id:"f11", memberId:"M-****48", tier:"Gold",     lastPurchase:"7 days ago",   topic:"Cordless Drills",      signalType:"upsell",       intentScore:88, action:"Add to Campaign"  },
    { id:"f12", memberId:"M-****82", tier:"Silver",   lastPurchase:"88 days ago",  topic:"Amazon — Hand Tools",  signalType:"reengagement", intentScore:74, competitor:"Amazon",       action:"Win-Back Now"     },
    { id:"f13", memberId:"M-****15", tier:"Platinum", lastPurchase:"2 days ago",   topic:"Outdoor Power Equipment",signalType:"upsell",     intentScore:90, action:"Upsell Bundle"    },
    { id:"f14", memberId:"M-****61", tier:"Gold",     lastPurchase:"41 days ago",  topic:"Paint & Supplies",     signalType:"crosssell",    intentScore:66, action:"Cross-Sell Push"  },
    { id:"f15", memberId:"M-****29", tier:"Silver",   lastPurchase:"110 days ago", topic:"Home Depot — Flooring",signalType:"reengagement", intentScore:81, competitor:"Home Depot",   action:"Re-Engage Now"    },
  ],
  "org-fmg": [
    { id:"f01", memberId:"M-****11", tier:"Gold",     lastPurchase:"3 days ago",   topic:"Organic Produce",      signalType:"upsell",       intentScore:78, action:"Add to Campaign"  },
    { id:"f02", memberId:"M-****44", tier:"Platinum", lastPurchase:"14 days ago",  topic:"Whole Foods — Dairy",  signalType:"competitor",   intentScore:89, competitor:"Whole Foods",   action:"Retention Alert"  },
    { id:"f03", memberId:"M-****77", tier:"Silver",   lastPurchase:"38 days ago",  topic:"Meal Kit Delivery",    signalType:"crosssell",    intentScore:74, action:"Cross-Sell Push"  },
    { id:"f04", memberId:"M-****22", tier:"Gold",     lastPurchase:"72 days ago",  topic:"Instacart Delivery",   signalType:"reengagement", intentScore:82, competitor:"Instacart",    action:"Win-Back Now"     },
    { id:"f05", memberId:"M-****55", tier:"Platinum", lastPurchase:"1 days ago",   topic:"Premium Seafood",      signalType:"upsell",       intentScore:86, action:"Upsell Bundle"    },
    { id:"f06", memberId:"M-****88", tier:"Silver",   lastPurchase:"48 days ago",  topic:"Health Supplements",   signalType:"crosssell",    intentScore:69, action:"Cross-Sell Push"  },
    { id:"f07", memberId:"M-****33", tier:"Gold",     lastPurchase:"95 days ago",  topic:"Trader Joe's",         signalType:"reengagement", intentScore:77, competitor:"Trader Joe's",  action:"Re-Engage Now"    },
    { id:"f08", memberId:"M-****66", tier:"Platinum", lastPurchase:"6 days ago",   topic:"Organic Baby Food",    signalType:"upsell",       intentScore:83, action:"Add to Campaign"  },
    { id:"f09", memberId:"M-****99", tier:"Gold",     lastPurchase:"22 days ago",  topic:"Whole Foods — Wine",   signalType:"competitor",   intentScore:91, competitor:"Whole Foods",   action:"Retention Alert"  },
    { id:"f10", memberId:"M-****44", tier:"Silver",   lastPurchase:"61 days ago",  topic:"Grocery Delivery",     signalType:"crosssell",    intentScore:72, action:"Inspire Campaign" },
    { id:"f11", memberId:"M-****77", tier:"Gold",     lastPurchase:"9 days ago",   topic:"Plant-Based Products", signalType:"upsell",       intentScore:80, action:"Add to Campaign"  },
    { id:"f12", memberId:"M-****11", tier:"Silver",   lastPurchase:"84 days ago",  topic:"Amazon Fresh",         signalType:"reengagement", intentScore:76, competitor:"Amazon Fresh",  action:"Win-Back Now"     },
  ],
  "org-sna": [
    { id:"f01", memberId:"M-****31", tier:"Gold",     lastPurchase:"5 days ago",   topic:"Athletic Leggings",    signalType:"upsell",       intentScore:82, action:"Add to Campaign"  },
    { id:"f02", memberId:"M-****64", tier:"Platinum", lastPurchase:"18 days ago",  topic:"Nike Running Shoes",   signalType:"competitor",   intentScore:93, competitor:"Nike",          action:"Retention Alert"  },
    { id:"f03", memberId:"M-****97", tier:"Silver",   lastPurchase:"42 days ago",  topic:"Sustainable Fashion",  signalType:"crosssell",    intentScore:71, action:"Cross-Sell Push"  },
    { id:"f04", memberId:"M-****28", tier:"Gold",     lastPurchase:"88 days ago",  topic:"Zara — Women's",       signalType:"reengagement", intentScore:79, competitor:"Zara",          action:"Win-Back Now"     },
    { id:"f05", memberId:"M-****61", tier:"Platinum", lastPurchase:"2 days ago",   topic:"Premium Denim",        signalType:"upsell",       intentScore:87, action:"Upsell Bundle"    },
    { id:"f06", memberId:"M-****94", tier:"Silver",   lastPurchase:"54 days ago",  topic:"Accessories",          signalType:"crosssell",    intentScore:67, action:"Cross-Sell Push"  },
    { id:"f07", memberId:"M-****37", tier:"Gold",     lastPurchase:"102 days ago", topic:"H&M — Basics",         signalType:"reengagement", intentScore:75, competitor:"H&M",           action:"Re-Engage Now"    },
    { id:"f08", memberId:"M-****70", tier:"Platinum", lastPurchase:"11 days ago",  topic:"Luxury Handbags",      signalType:"upsell",       intentScore:89, action:"Add to Campaign"  },
    { id:"f09", memberId:"M-****13", tier:"Gold",     lastPurchase:"29 days ago",  topic:"ASOS — Dresses",       signalType:"competitor",   intentScore:86, competitor:"ASOS",          action:"Retention Alert"  },
    { id:"f10", memberId:"M-****46", tier:"Silver",   lastPurchase:"67 days ago",  topic:"Footwear",             signalType:"crosssell",    intentScore:73, action:"Inspire Campaign" },
  ],
};

const SIGNAL_CONFIG = {
  upsell:       { label: "Upsell Opportunity",    color: "bg-blue/10 text-blue",          border: "border-blue/20",    icon: TrendingUp,   dot: "bg-blue"    },
  competitor:   { label: "Competitor Intent",      color: "bg-red-50 text-red-600",         border: "border-red-200",    icon: AlertTriangle,dot: "bg-red-500" },
  crosssell:    { label: "Cross-Sell Opportunity", color: "bg-cyan/10 text-cyan",           border: "border-cyan/20",    icon: ShoppingBag,  dot: "bg-cyan"    },
  reengagement: { label: "Re-Engagement Signal",   color: "bg-amber-50 text-amber-600",     border: "border-amber-200",  icon: RefreshCw,    dot: "bg-amber-400"},
};

const TIER_CONFIG = {
  Platinum: "bg-navy text-white",
  Gold:     "bg-amber-400 text-white",
  Silver:   "bg-gray-400 text-white",
};

// ── Insight summary per retailer ──────────────────────────────
const SUMMARIES: Record<string, { active: number; competitor: number; lapse: number; crosssell: number; topTopics: { topic: string; count: number }[]; competitors: { name: string; count: number }[] }> = {
  "org-mhw": {
    active: 340_000, competitor: 84_000, lapse: 12_400, crosssell: 92_000,
    topTopics: [
      { topic: "Power Tools",          count: 68_420 },
      { topic: "Outdoor & Garden",     count: 54_180 },
      { topic: "Smart Home Devices",   count: 41_230 },
      { topic: "Home Renovation",      count: 38_900 },
      { topic: "Electrical Supplies",  count: 29_640 },
    ],
    competitors: [
      { name: "Home Depot", count: 48_200 },
      { name: "Lowe's",     count: 28_400 },
      { name: "Amazon",     count: 7_400  },
    ],
  },
  "org-fmg": {
    active: 520_000, competitor: 124_000, lapse: 18_200, crosssell: 138_000,
    topTopics: [
      { topic: "Organic Produce",       count: 112_400 },
      { topic: "Meal Kit Delivery",     count: 84_200  },
      { topic: "Health Supplements",    count: 68_900  },
      { topic: "Premium Seafood",       count: 54_100  },
      { topic: "Plant-Based Products",  count: 42_800  },
    ],
    competitors: [
      { name: "Whole Foods", count: 62_400 },
      { name: "Instacart",   count: 38_200 },
      { name: "Trader Joe's",count: 23_400 },
    ],
  },
  "org-sna": {
    active: 184_000, competitor: 48_000, lapse: 8_400, crosssell: 52_000,
    topTopics: [
      { topic: "Athletic Wear",        count: 42_100 },
      { topic: "Sustainable Fashion",  count: 34_800 },
      { topic: "Premium Denim",        count: 28_400 },
      { topic: "Footwear",             count: 22_100 },
      { topic: "Accessories",          count: 18_400 },
    ],
    competitors: [
      { name: "Nike",  count: 22_400 },
      { name: "Zara",  count: 14_800 },
      { name: "ASOS",  count: 10_800 },
    ],
  },
};

function fmt(n: number): string {
  if (n >= 1_000_000) return `${(n/1_000_000).toFixed(1)}M`;
  if (n >= 1_000)     return `${(n/1_000).toFixed(0)}K`;
  return n.toLocaleString();
}

export default function LiveInsights({ retailerId }: { retailerId: string }) {
  const [paused, setPaused]       = useState(false);
  const [visibleCount, setVisible]= useState(8);
  const [counter, setCounter]     = useState(0);
  const feedRef                   = useRef<HTMLDivElement>(null);

  const entries  = FEED_DATA[retailerId] ?? FEED_DATA["org-mhw"];
  const summary  = SUMMARIES[retailerId] ?? SUMMARIES["org-mhw"];
  const retailer = RETAILERS.find(r => r.id === retailerId);

  // Slowly increment the "active now" counter
  useEffect(() => {
    const t = setInterval(() => {
      if (!paused) setCounter(c => (c + Math.floor(Math.random() * 3)) % 999);
    }, 2000);
    return () => clearInterval(t);
  }, [paused]);

  // Auto-scroll feed
  useEffect(() => {
    if (paused) return;
    const t = setInterval(() => {
      if (feedRef.current) {
        feedRef.current.scrollTop += 1;
        if (feedRef.current.scrollTop >= feedRef.current.scrollHeight - feedRef.current.clientHeight) {
          feedRef.current.scrollTop = 0;
        }
      }
    }, 40);
    return () => clearInterval(t);
  }, [paused]);

  return (
    <div className="space-y-4 animate-fade-in">

      {/* ── Hero banner ── */}
      <div className="rounded-xl overflow-hidden bg-dark border border-white/5">
        <div className="px-6 py-5">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
            <p className="text-white/60 text-xs uppercase tracking-widest">Live Signal Feed — {retailer?.name}</p>
          </div>
          <div className="grid grid-cols-3 gap-6">
            <div className="text-center">
              <p className="text-4xl font-black text-white tabular">{fmt(summary.active + counter)}</p>
              <p className="text-white/50 text-xs mt-1">loyalty members researching products right now</p>
              <div className="flex items-center justify-center gap-1 mt-2">
                <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                <span className="text-emerald-400 text-2xs font-semibold">Live · Updated every 60s</span>
              </div>
            </div>
            <div className="text-center border-x border-white/10">
              <p className="text-4xl font-black text-amber-400 tabular">{fmt(summary.competitor)}</p>
              <p className="text-white/50 text-xs mt-1">members showing competitor intent signals</p>
              <div className="flex items-center justify-center gap-1 mt-2">
                <AlertTriangle size={11} className="text-amber-400" />
                <span className="text-amber-400 text-2xs font-semibold">Retention Risk</span>
              </div>
            </div>
            <div className="text-center">
              <p className="text-4xl font-black text-red-400 tabular">{fmt(summary.lapse)}</p>
              <p className="text-white/50 text-xs mt-1">lapse-risk members with rising competitor research</p>
              <div className="flex items-center justify-center gap-1 mt-2">
                <AlertTriangle size={11} className="text-red-400" />
                <span className="text-red-400 text-2xs font-semibold">Urgent — Act Now</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Main content ── */}
      <div className="grid grid-cols-12 gap-4">

        {/* Feed */}
        <div className="col-span-8 space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
              <h3 className="text-sm font-semibold text-navy">Live Intent Feed</h3>
              <span className="badge badge-green text-2xs">Real-time</span>
            </div>
            <button onClick={() => setPaused(p => !p)}
              className={clsx("btn btn-sm", paused ? "btn-primary" : "btn-secondary")}>
              {paused ? "Resume" : "Pause"} Feed
            </button>
          </div>

          <div ref={feedRef} className="space-y-2 max-h-[520px] overflow-y-auto pr-1"
            onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}>
            {[...entries, ...entries].map((entry, i) => {
              const cfg = SIGNAL_CONFIG[entry.signalType];
              return (
                <div key={`${entry.id}-${i}`}
                  className={clsx("flex items-center gap-3 p-3 rounded-lg border bg-white transition-all duration-200 hover:shadow-card", cfg.border)}>

                  {/* Pulse dot */}
                  <div className={clsx("w-2 h-2 rounded-full flex-shrink-0", cfg.dot)} />

                  {/* Member info */}
                  <div className="flex items-center gap-2 flex-shrink-0 w-32">
                    <span className="text-xs font-mono text-gray-700">{entry.memberId}</span>
                    <span className={clsx("badge text-2xs px-1.5 py-0.5", TIER_CONFIG[entry.tier])}>{entry.tier}</span>
                  </div>

                  {/* Last purchase */}
                  <div className="flex-shrink-0 w-24">
                    <p className="text-2xs text-slate">Last purchase</p>
                    <p className="text-xs font-medium text-gray-700">{entry.lastPurchase}</p>
                  </div>

                  {/* Current research */}
                  <div className="flex-1 min-w-0">
                    <p className="text-2xs text-slate">Researching now</p>
                    <p className="text-xs font-semibold text-gray-800 truncate">{entry.topic}</p>
                  </div>

                  {/* Signal type */}
                  <div className="flex-shrink-0">
                    <span className={clsx("badge text-2xs", cfg.color)}>
                      <cfg.icon size={9} />
                      {entry.signalType === "competitor" && entry.competitor
                        ? `${entry.competitor}`
                        : cfg.label}
                    </span>
                  </div>

                  {/* Intent score */}
                  <div className="flex-shrink-0 text-center w-12">
                    <p className={clsx("text-sm font-black tabular",
                      entry.intentScore >= 85 ? "text-red-500" :
                      entry.intentScore >= 75 ? "text-amber-500" : "text-blue")}>
                      {entry.intentScore}
                    </p>
                    <p className="text-2xs text-slate">score</p>
                  </div>

                  {/* Action */}
                  <button className={clsx("btn btn-sm text-2xs flex-shrink-0 whitespace-nowrap",
                    entry.signalType === "competitor" || entry.signalType === "reengagement"
                      ? "btn-danger" : "btn-primary")}>
                    {entry.action}
                  </button>
                </div>
              );
            })}
          </div>
        </div>

        {/* Sidebar */}
        <div className="col-span-4 space-y-4">

          {/* Top intent topics */}
          <div className="card">
            <h3 className="text-xs font-semibold text-navy mb-3">Top Intent Topics Among Your Members</h3>
            <div className="space-y-2">
              {summary.topTopics.map((t, i) => (
                <div key={i}>
                  <div className="flex justify-between mb-0.5">
                    <span className="text-xs text-gray-700">{t.topic}</span>
                    <span className="text-xs font-semibold text-navy tabular">{fmt(t.count)}</span>
                  </div>
                  <div className="w-full bg-gray-100 rounded-full h-1.5">
                    <div className="bg-gradient-to-r from-blue to-cyan h-1.5 rounded-full"
                      style={{ width: `${(t.count / summary.topTopics[0].count) * 100}%` }} />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Competitor alert */}
          <div className="card border border-red-200 bg-red-50/50">
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle size={14} className="text-red-500" />
              <h3 className="text-xs font-semibold text-red-700">Competitor Alert</h3>
              <span className="badge badge-red text-2xs">{fmt(summary.competitor)} members</span>
            </div>
            <p className="text-xs text-slate mb-3">researching competitor products this week</p>
            <div className="space-y-2">
              {summary.competitors.map((c, i) => (
                <div key={i} className="flex items-center justify-between">
                  <span className="text-xs text-gray-700">{c.name}</span>
                  <div className="flex items-center gap-2">
                    <div className="w-20 bg-gray-100 rounded-full h-1.5">
                      <div className="bg-red-400 h-1.5 rounded-full"
                        style={{ width: `${(c.count / summary.competitors[0].count) * 100}%` }} />
                    </div>
                    <span className="text-xs font-semibold text-red-500 tabular w-10 text-right">{fmt(c.count)}</span>
                  </div>
                </div>
              ))}
            </div>
            <button className="btn-danger btn-sm w-full mt-3 flex items-center justify-center gap-1.5">
              <Zap size={12} /> Launch Retention Campaign
            </button>
          </div>

          {/* Cross-sell opps */}
          <div className="card border border-cyan/20 bg-cyan/5">
            <div className="flex items-center gap-2 mb-2">
              <ShoppingBag size={14} className="text-cyan" />
              <h3 className="text-xs font-semibold text-navy">Cross-Sell Opportunities</h3>
            </div>
            <p className="text-2xl font-black text-cyan tabular">{fmt(summary.crosssell)}</p>
            <p className="text-xs text-slate mt-0.5">members researching categories they've never purchased from you</p>
            <button className="btn-accent btn-sm w-full mt-3">Build Cross-Sell Audience</button>
          </div>

          {/* Lapse prevention */}
          <div className="card border border-amber-200 bg-amber-50/50">
            <div className="flex items-center gap-2 mb-2">
              <RefreshCw size={14} className="text-amber-500" />
              <h3 className="text-xs font-semibold text-navy">Lapse Prevention</h3>
            </div>
            <p className="text-2xl font-black text-amber-500 tabular">{fmt(summary.lapse)}</p>
            <p className="text-xs text-slate mt-0.5">high-risk members · avg lapse score: 78</p>
            <button className="btn btn-sm w-full mt-3 bg-amber-500 text-white hover:bg-amber-600">
              Run Win-Back Campaign
            </button>
          </div>
        </div>
      </div>

      {/* ── The MRN Difference ── */}
      <div className="card">
        <h3 className="text-sm font-semibold text-navy mb-4">The MRN Difference — What You Can See vs. What You're Missing</h3>
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
            <p className="text-xs font-bold text-slate mb-3 flex items-center gap-2">
              <Eye size={13} className="text-slate" /> What Your Loyalty Data Tells You
            </p>
            <div className="space-y-2">
              {[
                "Who bought what product",
                "When they last purchased",
                "How often they visit",
                "Their total spend and tier",
                "Their purchase history",
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-2">
                  <span className="text-slate text-xs">•</span>
                  <p className="text-xs text-gray-600">{item}</p>
                </div>
              ))}
            </div>
            <div className="mt-4 pt-3 border-t border-gray-200">
              <p className="text-xs font-semibold text-slate italic">Backward-looking. You only know what already happened.</p>
            </div>
          </div>
          <div className="bg-blue/5 rounded-xl p-4 border border-blue/20">
            <p className="text-xs font-bold text-blue mb-3 flex items-center gap-2">
              <Zap size={13} className="text-blue" /> What MRN Adds in Real Time
            </p>
            <div className="space-y-2">
              {[
                "What they're researching RIGHT NOW",
                "Which competitors they're considering",
                "Which new categories they're exploring",
                "Their lapse risk score before they leave",
                "Their cross-device reach — mobile, CTV, desktop",
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-2">
                  <span className="text-blue text-xs">✓</span>
                  <p className="text-xs text-gray-700 font-medium">{item}</p>
                </div>
              ))}
            </div>
            <div className="mt-4 pt-3 border-t border-blue/20">
              <p className="text-xs font-semibold text-blue italic">Forward-looking. You know what's about to happen.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}